<template>
  <a
    v-if="onActivate !== undefined"
    href="#"
    role="button"
    tabindex="0"
    class="footer-link"
    :class="legalLink ? 'footer-link--legal' : 'text-initial'"
    :aria-label="ariaLabel"
    :aria-disabled="disabled || undefined"
    @click.prevent="onActivate()"
    @keydown="keyboardHandler"
  >
    <slot />
  </a>
  <SBtn
    v-else
    :to="to"
    :href="href"
    :target="target"
    variant="text"
    class="footer-link"
    :class="legalLink ? 'footer-link--legal' : 'text-initial'"
    :disabled="disabled"
    no-prefetch
  >
    <slot />
  </SBtn>
</template>

<script lang="ts" setup>
const props = withDefaults(defineProps<{
  to?: string
  href?: string
  target?: string
  disabled?: boolean
  legalLink?: boolean
  ariaLabel?: string
  onActivate?: () => void
}>(), {
  to: undefined,
  href: undefined,
  target: undefined,
  disabled: false,
  legalLink: false,
  ariaLabel: undefined,
  onActivate: undefined
})

// When `on-activate` is provided the button is an action (e.g. open the cookie
// drawer), rendered as a keyboard-accessible <a role="button"> wired to both
// click and Enter/Space (the inline onclick it replaces was mouse-only). The
// anchor only renders under `v-if="onActivate !== undefined"`, so the template
// can call `onActivate()` directly — `.prevent` replaces a manual
// event.preventDefault(), and no in-handler onActivate guard is needed.
const keyboardHandler = useKeyboardActivation(() => props.onActivate?.())
</script>

<style lang="scss" scoped>
.footer-link {
  font-weight: 400;
  font-size: 14px;
  display: block;
  // text-transform applied per-variant: `&--legal` gets uppercase; non-legal
  // links get `.text-initial` from the conditional class binding (renders
  // source text unchanged, e.g. "Find a Demonstrator" with the article
  // lowercase). Base capitalize was always overridden in V2 (.text-initial
  // had !important there). V4's .text-initial lost that !important, letting
  // the base capitalize win and turn "Find a Demonstrator" into "Find A
  // Demonstrator". Removing capitalize from base produces the same final
  // behavior without re-adding !important to the global .text-initial rule.
  color: rgb(var(--v-theme-primary));
  justify-content: start;
  margin: 8px 0px;
  // V4's nested button architecture (overlay/underlay/prepend/content/append
  // spans) gives the outer button bbox an extra ~7px vs V2's simpler markup.
  // Explicit line-height + height collapses the bbox to match V2's 14px.
  line-height: 1;
  height: 14px;

  &--legal {
    // WCAG 1.4.3: see the matching rule in app/assets/scss/lib/typography.scss.
    color: rgb(var(--v-theme-footerLegal));
    font-size: 12px;
    text-transform: uppercase;
  }
}

</style>
